/**
 * Shared HTTP client for the portal application API (mttr/app_api).
 *
 * Every `services/*.ts` module used to hand-roll the same three things: resolve
 * `appApiBaseUrl` (+ strip trailing slashes), attach the `access-token` header +
 * `cache: "no-store"`, and parse a JSON error body into a throwable. This module
 * is the single copy of that logic.
 *
 * Behaviour is a strict superset of the old per-module helpers: `apiFetch`
 * throws `ApiError` (which `extends Error`, so `e instanceof Error` still holds)
 * carrying the HTTP `status`, and reads the error detail from `.error` (the
 * shape every mttr/app_api route emits) with `.detail` / `.message` fallbacks.
 * Modules whose own error class is instance-checked elsewhere wrap `apiFetch`
 * and rethrow that class — see e.g. `ticketsApi`, `inventoryApi`.
 */

import { configService } from "./configService";

/** Error thrown by {@link apiFetch}. `status` is the HTTP status when the
 *  failure came from a response (absent for a config/network failure). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Resolve the portal API base URL from runtime config, with any trailing
 * slashes stripped. `== null` (not falsy) so the empty-string same-origin base
 * used in production is accepted rather than treated as unset.
 */
export function apiBase(): string {
  const raw = configService.getConfig().mttr.appApiBaseUrl;
  if (raw == null) throw new ApiError("appApiBaseUrl is unset");
  return raw.replace(/\/+$/, "");
}

/**
 * Fetch `${apiBase()}${path}` as JSON.
 *
 * - Attaches `Accept: application/json`, the `access-token` header (when a token
 *   is supplied), and `Content-Type: application/json` when there's a body.
 *   Caller-supplied `init.headers` win over these defaults.
 * - `cache: "no-store"`.
 * - On a non-OK response, parses the JSON body and throws
 *   `ApiError(detail, status)` where `detail` is `.error` / `.detail` /
 *   `.message` (first string) or the status text.
 * - Returns the parsed JSON body; a 204 / empty body resolves to `undefined`.
 */
export async function apiFetch<T>(
  path: string,
  token: string | null | undefined,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(token ? { "access-token": token } : {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
    cache: "no-store",
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = (await res.json()) as {
        error?: unknown;
        detail?: unknown;
        message?: unknown;
      };
      const d = j?.error ?? j?.detail ?? j?.message;
      if (typeof d === "string") detail = d;
    } catch {
      /* no JSON body — keep the status text */
    }
    throw new ApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? (JSON.parse(text) as T) : (undefined as T));
}
